import { getWallPath } from "building-generation/elements/walls/wall";
import type { GeneratedDoorPlacement } from "building-types/doors/doorTypes";
import type { DoorSettings } from "building-types/settings/doorSettings";
import type { ActiveProfileSource } from "building-types/settings/profileCatalogSettings";
import type { WallCut } from "building-types/walls/wallCutTypes";
import { WallType } from "building-types/walls/wallEnums";
import type { WallSystem } from "building-types/walls/wallSytemTypes";
import type { GeneratedWindowPlacement } from "building-types/windows/windowTypes";
import { assertIsOneOf, assertIsRecordWithKeys, ensureDefined } from "common/utils/assertOfType";
import { isInteriorRoomEnclosure } from "doc/rooms/roomAttributesTools";
import { closestParamLine2 } from "geom/closestPoint";
import { vec2Dist } from "geom/vec2";
import { getDoorRoughOpeningAdjustment } from "../../doors/doorUtils";
import { WINDOW_FRAME_WIDTH } from "../../window/windowTools";
import { getWindowExteriorCasingOffsets, getWindowInteriorCasingOffsets } from "../solidWall";
import { getDoorExteriorCasingOffsets, getDoorInteriorCasingOffsets } from "../solidWallCrossSectionUtils";
import { subdivideWallIntervalsWithInterval } from "./wallInterval";
import {
  getDoorSwings,
  subdivideWallIntervalLoopWithDoorSwings,
  type DoorSwingIntervalInfo,
} from "./wallIntervalDoorSwing";
import {
  getFirstPlanOutlineVertexAlongTraversal,
  getLastPlanOutlineVertexAlongTraversal,
  makeWallInterval,
} from "./wallIntervalUtils";
import type { PlanOutlineEntity, WallInterval, WallIntervalLoop } from "./wallTraverseTypes";
import { WallIntervalEntityType } from "./wallTraverseTypes";

export const WINDOW_BOTTOM_HEIGHT_TO_IGNORE = 120;

function assertIsDoor(entity: PlanOutlineEntity): asserts entity is GeneratedDoorPlacement {
  assertIsRecordWithKeys(entity, ["type"]);
  assertIsOneOf(entity.type, ["DOOR", "DOOR_V2"]);
}

function assertIsWindow(entity: PlanOutlineEntity): asserts entity is GeneratedWindowPlacement {
  assertIsRecordWithKeys(entity, ["type"]);
  assertIsOneOf(entity.type, ["WINDOW", "WINDOW_V2"]);
}

function assertIsWallCut(entity: PlanOutlineEntity): asserts entity is WallCut {
  assertIsRecordWithKeys(entity, ["type"]);
  assertIsOneOf(entity.type, ["WALL_CUT"]);
}

interface MakeWallIntervalLoopOptions {
  readonly collinearEdgeIndices: number[][];
  readonly vertices: number[];
  readonly edgeIndexToHalfEdgeIndex: (number | undefined)[];
  readonly wallSystem: WallSystem;
  readonly profileSource: ActiveProfileSource;
  readonly doorSettings: DoorSettings;
}

export function addEntityIntervalsToWallIntervalLoop({
  vertices,
  collinearEdgeIndices,
  edgeIndexToHalfEdgeIndex,
  wallSystem,
  doorSettings,
  profileSource,
}: MakeWallIntervalLoopOptions): WallIntervalLoop {
  const loop: WallInterval[][] = [];
  const collinearJoinedVertices: number[] = [];
  const doorSwings: DoorSwingIntervalInfo[] = [];

  // use lookup to determine the edges a door or window vertex could land on
  for (const index of Object.keys(collinearEdgeIndices)) {
    const collinears = collinearEdgeIndices[index];

    // get the first vertex of the first index
    const firstIndex = collinears[0];
    const [x1, y1] = [vertices[firstIndex * 2], vertices[firstIndex * 2 + 1]];

    // get the last vertex of the last index
    const lastIndex = collinears[collinears.length - 1];
    const [x2, y2] = [vertices[lastIndex * 2 + 2], vertices[lastIndex * 2 + 3]];

    collinearJoinedVertices.push(x1, y1);

    // add last vertex if it is the last index
    if (+index === collinearEdgeIndices.length - 1) {
      collinearJoinedVertices.push(x2, y2);
    }

    const wallIntervalLength = vec2Dist(x1, y1, x2, y2);
    let intervals: WallInterval[] = [makeWallInterval(0, wallIntervalLength)];

    for (const edgeIndex of collinears) {
      const halfEdge = edgeIndexToHalfEdgeIndex[edgeIndex];
      if (halfEdge === undefined) {
        continue;
      }
      const wall = wallSystem.walls[wallSystem.halfEdgeToWall[halfEdge]];

      if (wall.wallType !== WallType.SOLID) {
        continue;
      }

      // depends on wall's left side being the half edge identified in each wall
      const wallPath = getWallPath(wall);
      const leftIsSide = wallPath.halfEdgeIndex === halfEdge;
      const frontRoom = leftIsSide ? wallPath.leftRoom : wallPath.rightRoom;
      const frontRoomIsExterior = !frontRoom || !isInteriorRoomEnclosure[frontRoom.attributes.roomEnclosure];

      const { leftSide, rightSide } = wall;
      const wallSide = leftIsSide ? leftSide : rightSide;
      const wallOppSide = leftIsSide ? rightSide : leftSide;

      const visitedEntityIds: { [entityId: string]: boolean } = {};

      const doorSideOffsetFunc = (door: PlanOutlineEntity) => {
        assertIsDoor(door);
        const { sideOffset } = frontRoomIsExterior
          ? getDoorExteriorCasingOffsets({ door })
          : getDoorInteriorCasingOffsets(door, profileSource);

        const roughOpeningAdjustment = getDoorRoughOpeningAdjustment(door, doorSettings);
        return sideOffset + roughOpeningAdjustment;
      };

      intervals = subdivideWallIntervalByEntities({
        entities: wallSide.doors,
        intervals,
        front: true,
        wallIntervalDescriptorType: WallIntervalEntityType.DOOR,
        visitedEntityIds,
        x1,
        y1,
        x2,
        y2,
        wallIntervalLength,
        sideOffsetFunc: doorSideOffsetFunc,
      });

      intervals = subdivideWallIntervalByEntities({
        entities: wallOppSide.doors,
        intervals,
        front: false,
        wallIntervalDescriptorType: WallIntervalEntityType.DOOR,
        visitedEntityIds,
        x1,
        y1,
        x2,
        y2,
        wallIntervalLength,
        sideOffsetFunc: doorSideOffsetFunc,
      });

      getDoorSwings(leftIsSide, wall, x1, y1, x2, y2, doorSwings, index);

      const windowSideOffsetFunc = (window: PlanOutlineEntity) => {
        assertIsWindow(window);
        const { sideOffset } = frontRoomIsExterior
          ? getWindowExteriorCasingOffsets({
              window,
              includeApronCasing: true,
              defaultWindowFrameWidth: WINDOW_FRAME_WIDTH,
            })
          : getWindowInteriorCasingOffsets();
        return sideOffset;
      };

      intervals = subdivideWallIntervalByEntities({
        entities: wallSide.windows,
        intervals,
        front: true,
        wallIntervalDescriptorType: WallIntervalEntityType.WINDOW,
        visitedEntityIds,
        x1,
        y1,
        x2,
        y2,
        wallIntervalLength,
        sideOffsetFunc: windowSideOffsetFunc,
      });

      intervals = subdivideWallIntervalByEntities({
        entities: wallOppSide.windows,
        intervals,
        front: false,
        wallIntervalDescriptorType: WallIntervalEntityType.WINDOW,
        visitedEntityIds,
        x1,
        y1,
        x2,
        y2,
        wallIntervalLength,
        sideOffsetFunc: windowSideOffsetFunc,
      });

      const wallCutSideOffsetFunc = (wallCut: PlanOutlineEntity) => {
        assertIsWallCut(wallCut);
        const { sideOffset } = frontRoomIsExterior
          ? { sideOffset: WINDOW_FRAME_WIDTH }
          : getWindowInteriorCasingOffsets();
        return sideOffset;
      };

      intervals = subdivideWallIntervalByEntities({
        entities: wallSide.wallCuts,
        intervals,
        front: true,
        wallIntervalDescriptorType: WallIntervalEntityType.WALL_CUT,
        visitedEntityIds,
        x1,
        y1,
        x2,
        y2,
        wallIntervalLength,
        sideOffsetFunc: wallCutSideOffsetFunc,
      });

      intervals = subdivideWallIntervalByEntities({
        entities: wallOppSide.wallCuts,
        intervals,
        front: false,
        wallIntervalDescriptorType: WallIntervalEntityType.WALL_CUT,
        visitedEntityIds,
        x1,
        y1,
        x2,
        y2,
        wallIntervalLength,
        sideOffsetFunc: wallCutSideOffsetFunc,
      });
    }

    loop.push(intervals);
  }

  subdivideWallIntervalLoopWithDoorSwings(loop, collinearJoinedVertices, doorSwings);

  return { collinearJoinedVertices, intervals: loop };
}

interface SubDivideWallIntervalsByEntityOptions {
  readonly entities: PlanOutlineEntity[];
  readonly intervals: WallInterval[];
  readonly front: boolean;
  readonly visitedEntityIds: { [entityId: string]: boolean };
  readonly x1: number;
  readonly y1: number;
  readonly x2: number;
  readonly y2: number;
  readonly wallIntervalLength: number;
  readonly wallIntervalDescriptorType: WallIntervalEntityType;
  readonly sideOffsetFunc?: (entity: PlanOutlineEntity) => number;
}

function subdivideWallIntervalByEntities({
  intervals,
  front,
  entities,
  wallIntervalDescriptorType,
  visitedEntityIds,
  x1,
  y1,
  x2,
  y2,
  wallIntervalLength,
  sideOffsetFunc = () => 0,
}: SubDivideWallIntervalsByEntityOptions) {
  for (const entity of entities) {
    if (entity.id) {
      if (visitedEntityIds[entity.id]) {
        continue;
      }
      visitedEntityIds[entity.id] = true;
      if (!entity?.opening?.planExtents) {
        continue;
      }
    }
    const planExtents = ensureDefined(entity?.opening?.planExtents);

    if (planExtents.length === 0) {
      continue;
    }

    const [rx, ry] = getFirstPlanOutlineVertexAlongTraversal({
      planOutline: planExtents,
      clockwise: false,
      frontEdge: front,
    });

    const r = closestParamLine2(x1, y1, x2, y2, rx, ry, true);

    const [lx, ly] = getLastPlanOutlineVertexAlongTraversal({
      planOutline: planExtents,
      clockwise: false,
      frontEdge: front,
    });

    const l = closestParamLine2(x1, y1, x2, y2, lx, ly, true);

    const sideOffset = sideOffsetFunc(entity);

    const rWithOffset = r - sideOffset;
    const finalR = rWithOffset < 0 ? 0 : rWithOffset;

    const lWithOffset = l + sideOffset;
    const finalL = lWithOffset > wallIntervalLength ? wallIntervalLength : lWithOffset;

    intervals = subdivideWallIntervalsWithInterval(
      intervals,
      makeWallInterval(finalR, finalL, wallIntervalDescriptorType, entity.id || undefined, front),
    );
  }
  return intervals;
}
